import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import box from './data/index'
import styles from './styles'
import { collectionGeneralSettings } from 'src/reducers/collection/action'
import { useDispatch } from 'react-redux'
import 'ckeditor5/ckeditor5.css'

import { withStyles } from '@material-ui/core'
import BoxButton from './components/box-button'
import DropdownBox from './components/dropdown-options'
import CKEditorComponent from './components/ck-editor'
import { useCourseSettings } from 'src/hooks/useCourseSettings'
import { useSelector } from 'react-redux'
import { initialThemeSettings } from 'src/model/theme'
import { editorEnum } from 'src/model/editor/editorEnum'
import { resolveFont, useWorkspaceBranding } from 'src/utils/workspace-logo'
import { loadFont } from 'src/pages/workspace/branding/font-select'

const CkEditor = props => {
  const { classes, saveData, initialData, font, fontSize, page, cardId } = props
  const editorRef = useRef(null)
  const dropdownRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedFeatureIndex, setSelectedFeatureIndex] = useState(0)
  const [doubleClickData, setDoubleClickData] = useState(null)
  const [language, setLanguage] = useState('English')
  // Memoize course settings to prevent unnecessary re-renders
  const languageSettings = useCourseSettings('language')
  const generalSettings = useCourseSettings('general')
  const themeSettings = useCourseSettings('theme')
  const dispatch = useDispatch()
  const pageUrl = window.location.pathname.split('/')[1]
  const pageUrlSub = window.location.pathname.split('/')[3]
  const courseSettings = useSelector(state => state.course.landingPageSettings)
  const collectionSettings = useSelector(
    state => state.collection.landingPageSettings,
  )
  const collection = useSelector(state => state.collection.collection)

  const getFilteredBox = useCallback(() => {
    return box
      .map(category => {
        if (category.value === 'outline') {
          if (
            pageUrl === 'mini-course' &&
            pageUrlSub !== 'completion' &&
            pageUrlSub !== 'landing-page'
          ) {
            return null
          }

          const filteredOptions = category.options.filter(option => {
            if (collection && collection.type === 1) {
              return (
                option.value === 'collectionList' ||
                option.value === 'catalog' ||
                option.value === 'continueLearning'
              )
            }
            if (pageUrl === 'collection') {
              return (
                option.value !== 'courseOutline1' &&
                option.value !== 'courseOutline2'
              )
            }

            if (pageUrl === 'mini-course' && pageUrlSub === 'completion') {
              return (
                option.value !== 'courseList1' &&
                option.value !== 'courseList2' &&
                option.value !== 'catalog' &&
                option.value !== 'continueLearning' &&
                option.value !== 'courseOutline1' &&
                option.value !== 'courseOutline2' &&
                option.value !== 'badge'
              )
            }
            if (pageUrl === 'mini-course' && pageUrlSub === 'landing-page') {
              return (
                option.value !== 'courseList1' &&
                option.value !== 'courseList2' &&
                option.value !== 'collectionList' &&
                option.value !== 'catalog' &&
                option.value !== 'continueLearning' &&
                option.value !== 'badge'
              )
            }

            return true
          })

          return {
            ...category,
            options: filteredOptions,
          }
        }

        return category
      })
      .filter(Boolean)
  }, [pageUrl, pageUrlSub, collection ? collection.type : null])
  const filteredBox = useMemo(() => {
    return getFilteredBox()
  }, [getFilteredBox])

  const valueSettings = useMemo(() => {
    return generalSettings.value || {}
  }, [generalSettings.value])

  const languageValue = useMemo(() => {
    return languageSettings.value
  }, [languageSettings.value])

  const languageExists = useMemo(() => {
    return languageSettings.exists
  }, [languageSettings.exists])

  // Memoize theme calculation
  const theme = useMemo(() => {
    const initialTheme = initialThemeSettings
    return pageUrl === 'mini-course'
      ? themeSettings.value
        ? themeSettings.value?.layout
        : initialTheme?.layout
      : 0
  }, [pageUrl, themeSettings.value])

  // Memoize landing page data
  const landingPageData = useMemo(() => {
    return pageUrl === 'mini-course'
      ? courseSettings || {}
      : collectionSettings || {}
  }, [pageUrl, courseSettings, collectionSettings])

  // Memoize the initial settings function to prevent recreation on every render
  const getInitialPageReaderSettings = useCallback(() => {
    if (
      valueSettings &&
      valueSettings.pageReaderSettings &&
      valueSettings.pageReaderSettings.textToSpeech
    ) {
      const tts = valueSettings.pageReaderSettings.textToSpeech
      return {
        textToSpeech: {
          value: typeof tts.value === 'boolean' ? tts.value : true,
          mode: tts.mode || (tts.value === 'advanced' ? 'advanced' : 'basic'),
          language: tts.language,
          voice: tts.voice || '',
        },
      }
    }
    return {
      textToSpeech: {
        value: false,
        mode: 'basic',
        voice: '',
      },
    }
  }, [valueSettings])

  // Memoize initial settings
  const initialPageReaderSettings = useMemo(() => {
    return getInitialPageReaderSettings()
  }, [getInitialPageReaderSettings])

  const [pageReaderSettings, setPageReaderSettings] = useState(
    initialPageReaderSettings,
  )

  // Only update pageReaderSettings when the memoized initial settings actually change
  useEffect(() => {
    const newSettings = getInitialPageReaderSettings()
    // Deep comparison to prevent unnecessary updates
    if (JSON.stringify(newSettings) !== JSON.stringify(pageReaderSettings)) {
      setPageReaderSettings(newSettings)
    }
  }, [getInitialPageReaderSettings])

  // Memoize languages to prevent recreation
  const languages = useMemo(() => {
    return window.CONSTANTS?.LANGUAGES || []
  }, [])

  // Language effect - only run when necessary values change
  useEffect(() => {
    // Set language based on course settings
    if (languageExists && languageValue?.selected && languages.length > 0) {
      const languageObj = languages.find(
        x => x.countryCode === languageValue.selected,
      )
      const newLanguage = languageObj ? languageObj.name : 'English'

      // Only update if language actually changed
      if (newLanguage !== language) {
        setLanguage(newLanguage)
      }
    } else if (language !== 'English') {
      setLanguage('English') // Default language
    }
  }, [languageExists, languageValue, languages, language])

  // Click outside effect
  useEffect(() => {
    const handleClickOutside = event => {
      // Check if click is inside dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Check if click is inside upgrade modal
        const isModalClick = event.target.closest('.MuiPopover-root') ||
          event.target.closest('[id*="dropdown-option-upgrade"]')

        // Only close dropdown if not clicking on modal
        if (!isModalClick) {
          setIsOpen(false)
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  const handleCategoryClick = useCallback(index => {
    setSelectedFeatureIndex(index)
    setIsOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Every catalog block gets its own id at insert time; the ordering saved in the
  // CatalogOrderItems table is keyed by it, so two blocks can be ordered independently.
  const withCatalogId = dataValues => {
    if (dataValues?.value !== 'catalog' || !dataValues.html) return dataValues

    const catalogId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`

    return {
      ...dataValues,
      html: dataValues.html.replace(
        /data-catalog-id=["'][^"']*["']/,
        `data-catalog-id="${catalogId}"`,
      ),
    }
  }

  const handleDragStart = useCallback(
    rawDataValues => event => {
      const dataValues = withCatalogId(rawDataValues)
      event.dataTransfer.clearData()
      const sampleHtml = dataValues.html
      const sampleElement = dataValues.element

      // Badge list: force default reward on new add
      if (dataValues.value === 'badge') {
        window.badgeListResetReward = true
      }

      // ✅ imageHotspot için özel handling
      const isImageHotspot = dataValues.value === 'imageHotspot'

      event.dataTransfer.setData('dragDropData', JSON.stringify(dataValues))
      event.dataTransfer.setData(
        'boxData',
        filteredBox[selectedFeatureIndex].value,
      )

      // DOM manipulation for textarea styling
      document.addEventListener('DOMContentLoaded', function () {
        let checkTextarea = setInterval(function () {
          const textarea = document.querySelector(
            '.ck.ck-reset.ck-input.ck-input-text.raw-html-embed__source',
          )
          if (textarea) {
            clearInterval(checkTextarea)
            setupVSCodeEffect(textarea)
          }
        }, 500)

        function setupVSCodeEffect(textarea) {
          const container = textarea.closest('.ck.ck-labeled-field-view')
          if (container) {
            container.style.marginTop = '28px'
            container.style.position = 'relative'
          }

          const copyButton = document.createElement('button')
          copyButton.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
              copy
          `
          copyButton.style.position = 'absolute'
          copyButton.style.top = '-22px'
          copyButton.style.right = '10px'
          copyButton.style.backgroundColor = 'rgba(60, 60, 60, 0.7)'
          copyButton.style.color = '#fff'
          copyButton.style.border = 'none'
          copyButton.style.borderRadius = '4px'
          copyButton.style.padding = '2px 8px'
          copyButton.style.fontSize = '12px'
          copyButton.style.cursor = 'pointer'
          copyButton.style.display = 'flex'
          copyButton.style.alignItems = 'center'
          copyButton.style.gap = '4px'
          copyButton.style.zIndex = '100'
          container.appendChild(copyButton)

          copyButton.addEventListener('click', function () {
            textarea.select()
            document.execCommand('copy')
            this.innerHTML = 'Copied!'
            setTimeout(() => {
              this.innerHTML = `
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                      copy
                  `
            }, 2000)
          })

          textarea.addEventListener('keydown', function (e) {
            if (e.key === 'Tab') {
              e.preventDefault()
              const start = this.selectionStart
              const end = this.selectionEnd

              this.value =
                this.value.substring(0, start) +
                '    ' +
                this.value.substring(end)
              this.selectionStart = this.selectionEnd = start + 4
            }
          })
        }
      })

      if (isImageHotspot) {
        const hotspotData = {
          ...dataValues,
          requiresImageSelection: true, // Flag to trigger image selection modal
        }
        event.dataTransfer.setData('dragDropData', JSON.stringify(hotspotData))
        event.dataTransfer.setData(
          'boxData',
          filteredBox[selectedFeatureIndex].value,
        )
        // Placeholder HTML - will be replaced with actual image
        if (sampleElement !== undefined) {
          event.dataTransfer.setData('element', JSON.stringify(sampleElement))
        } else if (sampleHtml) {
          event.dataTransfer.setData('text/html', sampleHtml)
        }
        return
      }

      // Original handling for other types
      if (filteredBox[selectedFeatureIndex].value !== 'embed') {
        if (
          (filteredBox[selectedFeatureIndex].value === 'actions' ||
            filteredBox[selectedFeatureIndex].value === 'activities') &&
          (dataValues.value === 'interactiveActivity' ||
            dataValues.value === 'pixelQuest' ||
            dataValues.value === 'wordle' ||
            dataValues.value === 'riddle' ||
            dataValues.value === 'jumble' ||
            dataValues.value === 'crossword' ||
            dataValues.value === 'wordSearch')
        ) {
          event.dataTransfer.setData('text/html', sampleHtml)
          event.dataTransfer.setData('dragDropData', JSON.stringify(dataValues))
          event.dataTransfer.setData(
            'boxData',
            filteredBox[selectedFeatureIndex].value,
          )
          event.dataTransfer.setData('font', font)
          event.dataTransfer.setData('language', language)
          event.dataTransfer.setData('languageCode', languageSettings?.value?.selected || 'en')
        } else {
          if (sampleElement !== undefined) {
            event.dataTransfer.setData('element', JSON.stringify(sampleElement))
          } else if (dataValues.value === 'imageCarousel') {
            // imageCarousel için text/html set etme - bizim handler'ımız işleyecek
            event.dataTransfer.setData('dragDropData', JSON.stringify(dataValues))
            event.dataTransfer.setData('boxData', filteredBox[selectedFeatureIndex].value)
          } else {
            event.dataTransfer.setData('text/html', sampleHtml)
          }
        }
      } else {
        event.dataTransfer.setData('text/html', sampleHtml)
        event.dataTransfer.setData('dragDropData', JSON.stringify(dataValues))
        event.dataTransfer.setData(
          'boxData',
          filteredBox[selectedFeatureIndex].value,
        )
      }
    },
    [selectedFeatureIndex, font, language, filteredBox, languageSettings],
  )

  const handleDoubleClick = useCallback(
    rawDataValues => {
      const dataValues = withCatalogId(rawDataValues)
      // ✅ imageHotspot için özel handling
      const isImageHotspot =
        filteredBox[selectedFeatureIndex].value === 'image' &&
        dataValues.value === 'imageHotspot'

      // Badge list: force default reward on new add
      if (dataValues.value === 'badge') {
        window.badgeListResetReward = true
      }

      if (isImageHotspot) {
        // imageHotspot için flag ekle
        setDoubleClickData({
          data: {
            ...dataValues,
            requiresImageSelection: true,
          },
          type: filteredBox[selectedFeatureIndex].value,
        })
      } else {
        // Normal handling
        setDoubleClickData({
          data: dataValues,
          type: filteredBox[selectedFeatureIndex].value,
        })
      }

      setIsOpen(false)
    },
    [selectedFeatureIndex, filteredBox],
  )

  const handleDrop = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Memoize font family calculation. A course or landing page left on the
  // default font shows the workspace branding's, as learners see it.
  const workspaceBranding = useWorkspaceBranding()
  const fontFamily = useMemo(
    () => resolveFont(page === 'landing-page' ? landingPageData.font : font),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, landingPageData.font, font, workspaceBranding],
  )
  // The branding's font may not be loaded on this screen yet.
  useEffect(() => {
    if (fontFamily) loadFont(fontFamily)
  }, [fontFamily])
  const landingPageSection = landingPageData?.sections?.find(
    x => x.type === editorEnum.headerType,
  )
  return (
    <div className={classes.CKEditor}>
      <CKEditorComponent
        key={`ck-editor-component-${landingPageSection?.content?.type || 1}`}
        pageReaderSettings={pageReaderSettings}
        editorRef={editorRef}
        cardId={cardId}
        onDropClose={handleDrop}
        saveData={saveData}
        initialData={initialData}
        doubleClickData={doubleClickData}
        setDoubleClickData={setDoubleClickData}
        fontSize={fontSize}
        fontFamily={fontFamily}
        page={page}
        theme={theme}
        language={language}
        languageSettings={languageSettings}
        setIsOpen={setIsOpen}
      />

      <BoxButton
        box={filteredBox}
        classes={classes}
        handleCategoryClick={handleCategoryClick}
        isOpen={isOpen}
      />

      {isOpen && (
        <div ref={dropdownRef}>
          <DropdownBox
            box={filteredBox}
            selectedFeatureIndex={selectedFeatureIndex}
            classes={classes}
            handleClose={handleClose}
            handleDragStart={handleDragStart}
            handleDoubleClick={handleDoubleClick}
            editorRef={editorRef}
          />
        </div>
      )}
    </div>
  )
}

export default withStyles(styles)(CkEditor)
